import {
  BarangayTabulation,
  MunicipalTabulation,
  Tabulation,
  User,
  VoterTrackStats
} from '../../types';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';

import TabulationGraph from './TabulationGraph';
import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';

const MunicipalStats: React.FC = () => {
  const { municipalId } = useParams<{ municipalId: string; }>();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [tabulations, setTabulations] = useState<Tabulation[]>([]);
  const [exitPollsStats, setExitPollsStats] = useState<VoterTrackStats>();
  const [finalReleasesStats, setFinalReleasesStats] = useState<VoterTrackStats>();
  const [stubReleasesStats, setStubReleasesStats] = useState<VoterTrackStats>();
  const [error, setError] = useState<string | null>(null);

  // Fetch stats from Firestore
  const fetchStatsFromFirestore = async () => {
    try {
      const exitPollsRef = doc(db, 'stats', 'exitPolls');
      const finalReleasesRef = doc(db, 'stats', 'finalReleases');
      const stubReleasesRef = doc(db, 'stats', 'stubReleases');

      const [exitPollsSnap, finalReleasesSnap, stubReleasesSnap] = await Promise.all([
        getDoc(exitPollsRef),
        getDoc(finalReleasesRef),
        getDoc(stubReleasesRef)
      ]);

      if (exitPollsSnap.exists()) {
        setExitPollsStats(exitPollsSnap.data() as VoterTrackStats);
      }
      if (finalReleasesSnap.exists()) {
        setFinalReleasesStats(finalReleasesSnap.data() as VoterTrackStats);
      }
      if (stubReleasesSnap.exists()) {
        setStubReleasesStats(stubReleasesSnap.data() as VoterTrackStats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to fetch stats');
    }
  };

  // Redirect if no view_throwing permission
  useEffect(() => {
    if (!loading && !user?.permissions.view_throwing) {
      navigate('/polls');
    }
  }, [user, loading, navigate]);

  const getTabulations = async (user: User, municipalId: string): Promise<void> => {
    try {
      const municipalDoc = doc(db, 'cities_municipalities', municipalId);
      const municipalSnap = await getDoc(municipalDoc);
      if (!municipalSnap.exists()) {
        console.error('Municipality not found');
        return;
      }
      const municipalInfo = municipalSnap.data();

      const cityTabDoc = doc(
        db,
        `tabulationsV2/${user.dashboard}/regions/REGION XIII/provinces/SURIGAO DEL NORTE/districts`,
        municipalInfo.district,
        'cities',
        municipalInfo.city_municipality
      );
      const cityTabSnap = await getDoc(cityTabDoc);
      const cityTab = cityTabSnap.data() as MunicipalTabulation;

      const barangayTabs: Tabulation[] = [];
      const citySnap = await getDocs(collection(cityTabDoc, 'barangays'));
      barangayTabs.push(...citySnap.docs.map((d) => d.data() as BarangayTabulation));

      const allTabulations = [
        cityTab,
        ...barangayTabs
      ];
      setTabulations(allTabulations);
    } catch (err) {
      console.error('Error fetching tabulations:', err);
      setError('Failed to fetch tabulations');
    }
  };

  useEffect(() => {
    if (user &&municipalId) {
      fetchStatsFromFirestore().then(() => {
        getTabulations(user, municipalId);
      });
    }
  }, [user, municipalId]);

  // If loading, return null to prevent flash of content
  if (loading) {
    return null;
  }
  
  return (
    <Box position="relative" height="100%" overflow="hidden">
      {error && (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      )}
      {tabulations.length > 0 && stubReleasesStats && finalReleasesStats && exitPollsStats ? (
        <Grid container spacing={1}>
          {tabulations.map((tabulation) => (
            <Grid item xs={12} md={tabulation.type === 'municipal' ? 12 : 6} key={tabulation.id}>
              <TabulationGraph
                key={tabulation.id}
                tabulation={tabulation}
                stubReleasesStats={stubReleasesStats}
                finalReleasesStats={finalReleasesStats}
                exitPollsStats={exitPollsStats}
                height={tabulation.type === 'municipal' ? 300 : 250}
                navUrl={tabulation.type === 'barangay' ? `/throwing/barangay/${(tabulation as BarangayTabulation).barangay}_${(tabulation as BarangayTabulation).city_municipality}_${(tabulation as BarangayTabulation).province}` : undefined}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default MunicipalStats;
